import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { useConfig } from '@/appconfig/ConfigContext';

import Logo from '../Logo';
import Spinner from '../Spinner';

type Props = { onDone?: () => void; isInactive?: boolean };

const SRC = 'https://d2rx4p52a2ycwl.cloudfront.net/Crave_Splash_720-1.mp4';
const MAX_SPLASH_MS = 15000;
const FADE_MS = 300;

const SplashLoader: React.FC<Props> = ({ onDone, isInactive = false }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [showVideo, setShowVideo] = useState(false);
  const [showPostSplash, setShowPostSplash] = useState(false);
  const [done, setDone] = useState(false);
  const config = useConfig();

  const startedRef = useRef(false);
  const doneRef = useRef(false);

  useEffect(() => {
    if (config && !config?.featureFlags?.animatedSplashVideo) {
      setShowPostSplash(true);
      setDone(true);
      finish();
    }
  }, [config]);

  const finish = useCallback(() => {
    if (doneRef.current) return;
    doneRef.current = true;

    setShowPostSplash(true);

    setShowVideo(false);

    const video = videoRef.current;
    video?.pause();
    video?.removeAttribute('src');
    video?.load();
    videoRef.current = null;

    window.setTimeout(() => {
      setDone(true);
      onDone?.();
    }, FADE_MS);
  }, [onDone]);

  useEffect(() => {
    if (!onDone) return;
    const id = window.setTimeout(finish, MAX_SPLASH_MS);
    return () => window.clearTimeout(id);
  }, [finish, onDone]);

  const start = async () => {
    if (startedRef.current || doneRef.current) return;
    startedRef.current = true;

    const video = videoRef.current;
    if (!video) return finish();
    video.playbackRate = 1;
    try {
      await video.play();
    } catch {
      try {
        video.muted = true;
        await video.play();
      } catch {
        finish();
      }
    }
  };

  const PostSplash = (
    <div className="absolute w-full h-full bg-primitive-brand-navy-blue">
      <div
        className="absolute left-1/2 top-1/2"
        style={{ transform: 'translate(-50%, -50%) scale(1.17)' }}
      >
        <Logo variant="xlarge" />
      </div>

      {!isInactive && (
        <div
          className="absolute left-1/2 top-1/2"
          style={{ transform: 'translate(-50%, 7.5rem)' }}
        >
          {/* Rendered directly rather than requested from SpinnerProvider: this
              is part of the splash composition (positioned under the logo), not
              a loading state competing with the rest of the app.
              The m-6 replaces the p-6 wrapper Spinner used to carry itself.
             */}
          <Spinner className="m-6" />
        </div>
      )}
    </div>
  );

  return (
    <div className="relative w-[120rem] h-[67.5rem] overflow-hidden">
      <div
        className={`absolute w-full h-full transition-opacity duration-300 ${
          showPostSplash ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        {PostSplash}
      </div>

      {!done && !isInactive && (
        <video
          ref={videoRef}
          src={SRC}
          playsInline
          preload="auto"
          controls={false}
          muted={false}
          loop={false}
          className={`absolute w-full h-full object-cover transition-opacity duration-300 ${
            showVideo ? 'opacity-100' : 'opacity-0'
          }`}
          onLoadedData={start}
          onPlaying={() => setShowVideo(true)}
          onEnded={finish}
        />
      )}
    </div>
  );
};

export default SplashLoader;
